import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";

const MoyasarPaymentForm = forwardRef(
  ({ amount, onSubmit, disabled = false, description }, ref) => {
    const formRef = useRef(null);
    const resolveRef = useRef(null);
    const { t, i18n } = useTranslation();


    useEffect(() => {
      const moyasarScript = document.createElement("script");
      moyasarScript.src = "https://cdn.moyasar.com/mpf/1.7.3/moyasar.js";
      moyasarScript.async = true;
      document.body.appendChild(moyasarScript);

      const moyasarCss = document.createElement("link");
      moyasarCss.href = "https://cdn.moyasar.com/mpf/1.7.3/moyasar.css";
      moyasarCss.rel = "stylesheet";
      document.head.appendChild(moyasarCss);

      const styleTag = document.createElement("style");
      styleTag.innerHTML = `
    .mysr-form input {
    direction: ltr !important;
    text-align: left !important;
  }

  .mysr-form input::placeholder {
    color: #999 !important;
    opacity: 0.7 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    letter-spacing: 0.3px !important;
  }

  .mysr-form input::-webkit-input-placeholder {
    color: #999 !important;
    opacity: 0.7 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    letter-spacing: 0.3px !important;
  }

  .mysr-form input::-moz-placeholder {
    color: #999 !important;
    opacity: 0.7 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    letter-spacing: 0.3px !important;
  }

  .mysr-form input:-ms-input-placeholder {
    color: #999 !important;
    opacity: 0.7 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    letter-spacing: 0.3px !important;
  }

  .mysr-form-button {
    font-size: 18px !important;
    padding: 16px 24px !important;
    height: auto !important;
    min-height: 60px !important;
  }
  `;
      document.head.appendChild(styleTag);
      const countryId = localStorage.getItem("countryId");
      const isSaudi = countryId === "1";
      const currency = isSaudi ? "SAR" : "BHD";
      const adjustedAmount = isSaudi ? amount : amount * 10; // Convert BHD 0.250 to 2.5
      const provider2Base = (process.env.REACT_APP_PROVIDER2 || "").replace(/\/+$/, "");

      moyasarScript.onload = () => {
        console.log("✅ Moyasar script loaded, amount:", description);
        console.log(`${provider2Base}/orders/moyasar/invoice`);
        
        // CRITICAL: Validate orderId exists and is recent before initializing payment
        const orderId = localStorage.getItem("orderId");
        const orderTimestamp = localStorage.getItem("orderIdTimestamp");
        
        if (!orderId) {
          console.error("❌ No orderId found - cannot initialize payment");
          return;
        }
        
        // Validate orderId is recent (within last hour)
        if (orderTimestamp) {
          const timestamp = parseInt(orderTimestamp, 10);
          const now = Date.now();
          const oneHour = 60 * 60 * 1000;
          if ((now - timestamp) >= oneHour) {
            console.error("❌ OrderId is stale (older than 1 hour) - cannot initialize payment");
            return;
          }
        }
        
        if (window.Moyasar && amount && orderId) {
          window.Moyasar.init({
            element: ".mysr-form",
            amount: adjustedAmount * 100,
            currency: currency,
            description: description || "AlRomansiah QR Order",
            metadata: {
              orderId: orderId
            },

            language: "en",
            publishable_api_key: process.env.REACT_APP_MOYSER,
            callback_url: `${window.location.origin}/payment-check-moyasar?amount=${adjustedAmount * 100}`,
            webhook_url: `${provider2Base}/orders/moyasar/invoice`,
            methods: ["creditcard", "applepay"],
            apple_pay: {
              type: "applepay",
              label: "pay with apple",
              validate_merchant_url:
                "https://api.moyasar.com/v1/applepay/initiate",
              country: localStorage.getItem("countryId") == "1" ? "SA" : "BH",
            },
            fixed_width: false,
            on_loaded: () => {
              const formElement = document.querySelector(".mysr-form");

              // Fix input direction and set professional placeholder text
              const inputElements =
                document.querySelectorAll(".mysr-form input");
              inputElements.forEach((input) => {
                input.setAttribute("dir", "ltr");
                input.style.textAlign = "left";
              });
              
              // Set professional placeholder text for card number field
              // Check for placeholder with long number strings and replace with formatted version
              setTimeout(() => {
                const allInputs = document.querySelectorAll(".mysr-form input");
                allInputs.forEach((input) => {
                  const placeholder = input.getAttribute("placeholder") || "";
                  // Check if placeholder contains a long unformatted number (12+ digits)
                  if (placeholder.match(/\d{12,}/) && !placeholder.includes(" ")) {
                    input.setAttribute("placeholder", "1234 5678 9012 3456");
                  }
                });
              }, 200);

              // Style hint text/placeholders better
              const style = document.createElement("style");
              style.textContent = `
                .mysr-form input::placeholder {
                  color: #999 !important;
                  opacity: 0.7 !important;
                  font-size: 14px !important;
                  font-weight: 400 !important;
                  letter-spacing: 0.3px !important;
                }
                .mysr-form label {
                  color: #666 !important;
                  font-size: 13px !important;
                  font-weight: 500 !important;
                  margin-bottom: 6px !important;
                }
              `;
              if (!document.getElementById("moyasar-hint-styles")) {
                style.id = "moyasar-hint-styles";
                document.head.appendChild(style);
              }

              const separator = document.querySelector(
                ".mysr-methods-separator"
              );
              if (separator) separator.style.display = "none";

              // ❗ Disable the pay button if disabled is true
              const payButton = document.querySelector(".mysr-form-button");
              if (payButton && disabled) {
                payButton.setAttribute("disabled", "true");
                payButton.style.opacity = "0.5";
                payButton.style.cursor = "not-allowed";
              }
            },
            on_initiating: () => {
              return new Promise(async (resolve, reject) => {
                resolveRef.current = resolve;
                resolve(true);
              });
            },
          });

          if (formRef.current) {
            formRef.current.addEventListener("submit", (event) => {
              event.preventDefault();
            });
          }
        } else {
          console.log("✅ Moyasar script loaded, amount:", amount);
        }
      };

      return () => {
        document.body.removeChild(moyasarScript);
        document.head.removeChild(moyasarCss);
      };
    }, [amount, onSubmit]);

    // Expose the formRef to parent via forwarded ref
    useImperativeHandle(ref, () => ({
      submitForm: () => {
        const payButton = document.querySelector(".mysr-form-button");
        if (payButton) {
          payButton.click(); // Simulate a click on the Moyasar "Pay" button
        } else {
          console.error("Moyasar pay button not found.");
        }
      },
    }));

    return (
      <section
        className="moyasar-payment-section"
        style={{
          direction: "ltr",
          textAlign: "left",
          marginTop: "1.5em",
        }}
      >
        <div className="mysr-form" ref={formRef}></div>
      </section>
    );
  }
);

export default MoyasarPaymentForm;
